/**
 * Location-gated FusionMSI marketing pages — static config only.
 * Add a country by adding a top-level key; no router or component changes required.
 *
 * Coordinates use { lat, lng } (~6 dp) — same convention as haversineKm and CITY_COORDINATES.
 */

/** Root segments reserved by existing marketing / product routes — keep in sync with fusionMsiMarketingRouting.js */
const RESERVED_ROOT_SEGMENTS = new Set([
  'how-it-works',
  'capabilities',
  'access',
  'patient',
  'clinician',
  'facility',
  'fde',
  'for-executives',
  'get-started',
  'about',
  'business-model',
  'historical-precedent',
  'platform',
  'auth',
  'signup',
  'join',
  'display',
  'admin',
  'review',
  'sources',
  'dp',
  'cities1',
  'flash13',
  'splashpage',
]);

/**
 * @typedef {object} GeoFacility
 * @property {string} id
 * @property {string} name
 * @property {string} [description]
 * @property {number} lat
 * @property {number} lng
 */

/**
 * @typedef {object} GeoHeroCopy
 * @property {string} [eyebrow]
 * @property {string} headline
 * @property {string} subhead
 */

/**
 * @typedef {object} GeoCta
 * @property {string} label
 * @property {string} to — internal path key: hub | platform | clinician | patient | facility | fde
 */

export const GEO_MARKETING_DATA = {
  kenya: {
    slug: 'kenya',
    countryName: 'Kenya',
    lat: -0.023559,
    lng: 37.906193,
    heroCopy: {
      eyebrow: 'Applied SuperIntelligence · East Africa',
      headline: 'Clinical AI that compounds where every interaction matters.',
      subhead:
        'In resource-limited settings, a learning loop is most powerful precisely where each patient encounter counts. FusionMSI and the ASI platform help mission hospitals and regional teams build institutional knowledge — not replace it.',
    },
    ctas: [
      { label: 'Explore the platform', to: 'platform' },
      { label: 'For clinicians', to: 'clinician' },
    ],
    facilities: [],
    cities: {
      nairobi: {
        slug: 'nairobi',
        cityName: 'Nairobi',
        lat: -1.286389,
        lng: 36.817223,
        heroCopy: {
          eyebrow: 'Nairobi · Kenya',
          headline: 'Frontier clinical AI for Kenya\'s major urban centre.',
          subhead:
            'Nairobi anchors East Africa\'s referral networks and training pipelines. FusionMSI brings retrieve-never-generate bedside reference and the ASI cognitive feedback loop to teams serving high-acuity volumes in the capital region.',
        },
        ctas: [
          { label: 'Platform overview', to: 'platform' },
          { label: 'For facility leaders', to: 'facility' },
        ],
        // PLACEHOLDER — no Kenya facilities in careverify.seed.json; add verified entries when available.
        facilities: [],
      },
    },
  },
};

/** @param {string} segment */
export function isReservedMarketingSegment(segment) {
  if (!segment || typeof segment !== 'string') return true;
  return RESERVED_ROOT_SEGMENTS.has(segment.toLowerCase());
}

/** @returns {string[]} */
export function listGeoCountries() {
  return Object.keys(GEO_MARKETING_DATA);
}

/**
 * @param {string} countrySlug
 * @returns {typeof GEO_MARKETING_DATA[string] | null}
 */
export function getGeoCountry(countrySlug) {
  if (!countrySlug || isReservedMarketingSegment(countrySlug)) return null;
  return GEO_MARKETING_DATA[countrySlug.toLowerCase()] ?? null;
}

/**
 * @param {string} countrySlug
 * @param {string} citySlug
 */
export function getGeoCity(countrySlug, citySlug) {
  const country = getGeoCountry(countrySlug);
  if (!country || !citySlug) return null;
  return country.cities?.[citySlug.toLowerCase()] ?? null;
}

/** @param {string} countrySlug */
export function hasGeoCountry(countrySlug) {
  return Boolean(getGeoCountry(countrySlug));
}

/**
 * Flat list of geo match targets for nearest-distance lookup (PROMPT B).
 * @returns {Array<{ type: 'city'|'country', countrySlug: string, citySlug?: string, label: string, lat: number, lng: number }>}
 */
export function listGeoMatchTargets() {
  const targets = [];
  Object.values(GEO_MARKETING_DATA).forEach((country) => {
    targets.push({
      type: 'country',
      countrySlug: country.slug,
      label: country.countryName,
      lat: country.lat,
      lng: country.lng,
    });
    Object.values(country.cities || {}).forEach((city) => {
      targets.push({
        type: 'city',
        countrySlug: country.slug,
        citySlug: city.slug,
        label: `${city.cityName}, ${country.countryName}`,
        lat: city.lat,
        lng: city.lng,
      });
    });
  });
  return targets;
}
